// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Timezone Context
 *
 * Manages user and practice timezone preferences for the application.
 * The effective timezone follows this priority:
 * 1. User's personal timezone preference (if set)
 * 2. Practice/Organization timezone (required - no browser fallback)
 *
 * This context provides utility functions for formatting dates and times
 * in the effective timezone.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { showNotification } from '@mantine/notifications';
import { useOrganization } from './OrganizationContext';
import { getAppSettings } from '../utils/organizationSettings';
import {
  buildUserPreferencesExtension,
  getUserPreferences,
  mergeUserPreferences,
  setPractitionerExtension,
  USER_EXTENSION_URLS,
} from '../utils/userPreferences';

// Default timezone if nothing is configured (used as fallback during initial setup)
const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Get the UTC offset for a timezone in minutes
 */
function getTimezoneOffsetMinutes(timezone: string): number {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    if (!offsetPart) return 0;

    // Parse offset like "GMT-5", "GMT+5:30", "GMT"
    const regex = /GMT([+-])?(\d+)?(?::(\d+))?/;
    const match = regex.exec(offsetPart.value);
    if (!match) return 0;

    const sign = match[1] === '-' ? -1 : 1;
    const hours = Number.parseInt(match[2] || '0', 10);
    const minutes = Number.parseInt(match[3] || '0', 10);
    return sign * (hours * 60 + minutes);
  } catch {
    return 0;
  }
}

/**
 * Format UTC offset as string like "(UTC-05:00)" or "(UTC+09:00)"
 */
function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return `(UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')})`;
}

// Comprehensive list of all IANA timezones with healthcare-standard display names
// Display format: "(UTC-05:00) Eastern Time (US & Canada)"
// The value is the IANA zone identifier, label is the user-friendly display name
export const TIMEZONES = [
  // UTC
  { value: 'UTC', label: 'Coordinated Universal Time', region: 'UTC' },

  // Africa
  { value: 'Africa/Abidjan', label: 'Greenwich Mean Time (Abidjan)', region: 'Africa' },
  { value: 'Africa/Accra', label: 'Greenwich Mean Time (Accra)', region: 'Africa' },
  { value: 'Africa/Addis_Ababa', label: 'East Africa Time (Addis Ababa)', region: 'Africa' },
  { value: 'Africa/Algiers', label: 'Central European Time (Algiers)', region: 'Africa' },
  { value: 'Africa/Cairo', label: 'Eastern European Time (Cairo)', region: 'Africa' },
  { value: 'Africa/Casablanca', label: 'Western European Time (Casablanca)', region: 'Africa' },
  { value: 'Africa/Dakar', label: 'Greenwich Mean Time (Dakar)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'West Africa Time (Lagos)', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'East Africa Time (Nairobi)', region: 'Africa' },
  { value: 'Africa/Tunis', label: 'Central European Time (Tunis)', region: 'Africa' },

  // Americas - US (Primary options for US healthcare)
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)', region: 'US' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)', region: 'US' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)', region: 'US' },
  { value: 'America/Phoenix', label: 'Arizona Time (No DST)', region: 'US' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', region: 'US' },
  { value: 'America/Anchorage', label: 'Alaska Time', region: 'US' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian Time (Adak)', region: 'US' },
  { value: 'America/Boise', label: 'Mountain Time (Boise)', region: 'US' },
  { value: 'America/Detroit', label: 'Eastern Time (Detroit)', region: 'US' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern Time (Indianapolis)', region: 'US' },
  { value: 'America/Kentucky/Louisville', label: 'Eastern Time (Louisville)', region: 'US' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Standard Time', region: 'US' },

  // Americas - Canada
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)', region: 'Canada' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)', region: 'Canada' },
  { value: 'America/Edmonton', label: 'Mountain Time (Edmonton)', region: 'Canada' },
  { value: 'America/Winnipeg', label: 'Central Time (Winnipeg)', region: 'Canada' },
  { value: 'America/Halifax', label: 'Atlantic Time (Halifax)', region: 'Canada' },
  { value: 'America/St_Johns', label: 'Newfoundland Time', region: 'Canada' },
  { value: 'America/Regina', label: 'Central Standard Time (Saskatchewan)', region: 'Canada' },
  { value: 'America/Montreal', label: 'Eastern Time (Montreal)', region: 'Canada' },
  { value: 'America/Calgary', label: 'Mountain Time (Calgary)', region: 'Canada' },

  // Americas - Mexico
  { value: 'America/Mexico_City', label: 'Central Time (Mexico City)', region: 'Mexico' },
  { value: 'America/Cancun', label: 'Eastern Standard Time (Cancun)', region: 'Mexico' },
  { value: 'America/Tijuana', label: 'Pacific Time (Tijuana)', region: 'Mexico' },
  { value: 'America/Monterrey', label: 'Central Time (Monterrey)', region: 'Mexico' },

  // Americas - Central America
  { value: 'America/Guatemala', label: 'Central Standard Time (Guatemala)', region: 'Central America' },
  { value: 'America/Belize', label: 'Central Standard Time (Belize)', region: 'Central America' },
  { value: 'America/Costa_Rica', label: 'Central Standard Time (Costa Rica)', region: 'Central America' },
  { value: 'America/El_Salvador', label: 'Central Standard Time (El Salvador)', region: 'Central America' },
  { value: 'America/Honduras', label: 'Central Standard Time (Honduras)', region: 'Central America' },
  { value: 'America/Managua', label: 'Central Standard Time (Managua)', region: 'Central America' },
  { value: 'America/Panama', label: 'Eastern Standard Time (Panama)', region: 'Central America' },

  // Americas - Caribbean
  { value: 'America/Puerto_Rico', label: 'Atlantic Standard Time (Puerto Rico)', region: 'Caribbean' },
  { value: 'America/Jamaica', label: 'Eastern Standard Time (Jamaica)', region: 'Caribbean' },
  { value: 'America/Havana', label: 'Cuba Time (Havana)', region: 'Caribbean' },
  { value: 'America/Santo_Domingo', label: 'Atlantic Standard Time (Santo Domingo)', region: 'Caribbean' },
  { value: 'America/Port-au-Prince', label: 'Eastern Time (Port-au-Prince)', region: 'Caribbean' },
  { value: 'America/Nassau', label: 'Eastern Time (Nassau)', region: 'Caribbean' },
  { value: 'America/Barbados', label: 'Atlantic Standard Time (Barbados)', region: 'Caribbean' },
  { value: 'America/Martinique', label: 'Atlantic Standard Time (Martinique)', region: 'Caribbean' },
  { value: 'America/Curacao', label: 'Atlantic Standard Time (Curacao)', region: 'Caribbean' },
  { value: 'America/Aruba', label: 'Atlantic Standard Time (Aruba)', region: 'Caribbean' },
  { value: 'America/Grand_Turk', label: 'Eastern Time (Grand Turk)', region: 'Caribbean' },
  { value: 'America/Cayman', label: 'Eastern Standard Time (Cayman)', region: 'Caribbean' },
  { value: 'America/St_Thomas', label: 'Atlantic Standard Time (US Virgin Islands)', region: 'Caribbean' },
  { value: 'America/Tortola', label: 'Atlantic Standard Time (British Virgin Islands)', region: 'Caribbean' },

  // Americas - South America
  { value: 'America/Sao_Paulo', label: 'Brasilia Time (São Paulo)', region: 'South America' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time (Buenos Aires)', region: 'South America' },
  { value: 'America/Bogota', label: 'Colombia Time (Bogota)', region: 'South America' },
  { value: 'America/Lima', label: 'Peru Time (Lima)', region: 'South America' },
  { value: 'America/Santiago', label: 'Chile Time (Santiago)', region: 'South America' },
  { value: 'America/Caracas', label: 'Venezuela Time (Caracas)', region: 'South America' },
  { value: 'America/Guayaquil', label: 'Ecuador Time (Guayaquil)', region: 'South America' },
  { value: 'America/La_Paz', label: 'Bolivia Time (La Paz)', region: 'South America' },
  { value: 'America/Montevideo', label: 'Uruguay Time (Montevideo)', region: 'South America' },
  { value: 'America/Asuncion', label: 'Paraguay Time (Asunción)', region: 'South America' },
  { value: 'America/Paramaribo', label: 'Suriname Time (Paramaribo)', region: 'South America' },
  { value: 'America/Guyana', label: 'Guyana Time', region: 'South America' },

  // Asia
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore Time', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (Seoul)', region: 'Asia' },
  { value: 'Asia/Taipei', label: 'Taipei Standard Time', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (Bangkok)', region: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time (Jakarta)', region: 'Asia' },
  { value: 'Asia/Manila', label: 'Philippine Time (Manila)', region: 'Asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia Time (Kuala Lumpur)', region: 'Asia' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Indochina Time (Ho Chi Minh)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India Standard Time', region: 'Asia' },
  { value: 'Asia/Mumbai', label: 'India Standard Time (Mumbai)', region: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (Dhaka)', region: 'Asia' },
  { value: 'Asia/Karachi', label: 'Pakistan Standard Time (Karachi)', region: 'Asia' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)', region: 'Asia' },
  { value: 'Asia/Riyadh', label: 'Arabia Standard Time (Riyadh)', region: 'Asia' },
  { value: 'Asia/Jerusalem', label: 'Israel Standard Time (Jerusalem)', region: 'Asia' },
  { value: 'Asia/Tel_Aviv', label: 'Israel Standard Time (Tel Aviv)', region: 'Asia' },
  { value: 'Asia/Beirut', label: 'Eastern European Time (Beirut)', region: 'Asia' },
  { value: 'Asia/Baghdad', label: 'Arabia Standard Time (Baghdad)', region: 'Asia' },
  { value: 'Asia/Tehran', label: 'Iran Standard Time (Tehran)', region: 'Asia' },
  { value: 'Asia/Kabul', label: 'Afghanistan Time (Kabul)', region: 'Asia' },
  { value: 'Asia/Almaty', label: 'Alma-Ata Time (Almaty)', region: 'Asia' },
  { value: 'Asia/Tashkent', label: 'Uzbekistan Time (Tashkent)', region: 'Asia' },
  { value: 'Asia/Kathmandu', label: 'Nepal Time (Kathmandu)', region: 'Asia' },
  { value: 'Asia/Colombo', label: 'Sri Lanka Standard Time (Colombo)', region: 'Asia' },
  { value: 'Asia/Yangon', label: 'Myanmar Time (Yangon)', region: 'Asia' },
  { value: 'Asia/Phnom_Penh', label: 'Indochina Time (Phnom Penh)', region: 'Asia' },
  { value: 'Asia/Vladivostok', label: 'Vladivostok Time', region: 'Asia' },

  // Australia & Pacific
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)', region: 'Australia' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)', region: 'Australia' },
  { value: 'Australia/Brisbane', label: 'Australian Eastern Standard Time (Brisbane)', region: 'Australia' },
  { value: 'Australia/Perth', label: 'Australian Western Standard Time (Perth)', region: 'Australia' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time (Adelaide)', region: 'Australia' },
  { value: 'Australia/Hobart', label: 'Australian Eastern Time (Hobart)', region: 'Australia' },
  { value: 'Australia/Darwin', label: 'Australian Central Standard Time (Darwin)', region: 'Australia' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (Auckland)', region: 'Pacific' },
  { value: 'Pacific/Wellington', label: 'New Zealand Time (Wellington)', region: 'Pacific' },
  { value: 'Pacific/Fiji', label: 'Fiji Time', region: 'Pacific' },
  { value: 'Pacific/Guam', label: 'Chamorro Standard Time (Guam)', region: 'Pacific' },
  { value: 'Pacific/Samoa', label: 'Samoa Standard Time', region: 'Pacific' },
  { value: 'Pacific/Tahiti', label: 'Tahiti Time', region: 'Pacific' },
  { value: 'Pacific/Port_Moresby', label: 'Papua New Guinea Time', region: 'Pacific' },

  // Europe
  { value: 'Europe/London', label: 'Greenwich Mean Time (London)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European Time (Paris)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Central European Time (Berlin)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Central European Time (Madrid)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Central European Time (Rome)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (Amsterdam)', region: 'Europe' },
  { value: 'Europe/Brussels', label: 'Central European Time (Brussels)', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Central European Time (Vienna)', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Central European Time (Zurich)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Central European Time (Stockholm)', region: 'Europe' },
  { value: 'Europe/Oslo', label: 'Central European Time (Oslo)', region: 'Europe' },
  { value: 'Europe/Copenhagen', label: 'Central European Time (Copenhagen)', region: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (Helsinki)', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Central European Time (Warsaw)', region: 'Europe' },
  { value: 'Europe/Prague', label: 'Central European Time (Prague)', region: 'Europe' },
  { value: 'Europe/Budapest', label: 'Central European Time (Budapest)', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Eastern European Time (Athens)', region: 'Europe' },
  { value: 'Europe/Bucharest', label: 'Eastern European Time (Bucharest)', region: 'Europe' },
  { value: 'Europe/Sofia', label: 'Eastern European Time (Sofia)', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (Istanbul)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time', region: 'Europe' },
  { value: 'Europe/Kiev', label: 'Eastern European Time (Kyiv)', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'Greenwich Mean Time (Dublin)', region: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Western European Time (Lisbon)', region: 'Europe' },
  { value: 'Europe/Reykjavik', label: 'Greenwich Mean Time (Reykjavik)', region: 'Europe' },

  // Atlantic
  { value: 'Atlantic/Bermuda', label: 'Atlantic Time (Bermuda)', region: 'Atlantic' },
  { value: 'Atlantic/Azores', label: 'Azores Time', region: 'Atlantic' },
  { value: 'Atlantic/Canary', label: 'Western European Time (Canary Islands)', region: 'Atlantic' },
  { value: 'Atlantic/Cape_Verde', label: 'Cape Verde Time', region: 'Atlantic' },

  // Indian Ocean
  { value: 'Indian/Mauritius', label: 'Mauritius Time', region: 'Indian Ocean' },
  { value: 'Indian/Maldives', label: 'Maldives Time', region: 'Indian Ocean' },
  { value: 'Indian/Reunion', label: 'Réunion Time', region: 'Indian Ocean' },
];

/**
 * Generate timezone select data with UTC offsets, sorted by offset
 * Format: "(UTC-05:00) Eastern Time (US and Canada)"
 */
export function getTimezoneSelectData(): { value: string; label: string; offset: number }[] {
  const data = TIMEZONES.map((tz) => {
    const offset = getTimezoneOffsetMinutes(tz.value);
    const offsetStr = formatUtcOffset(offset);
    return {
      value: tz.value,
      label: `${offsetStr} ${tz.label}`,
      offset,
    };
  });

  // Sort by UTC offset (most negative first), then by label
  return data.sort((a, b) => {
    if (a.offset !== b.offset) {
      return a.offset - b.offset;
    }
    return a.label.localeCompare(b.label);
  });
}

export interface TimezoneContextValue {
  /** The effective timezone for the current user (user preference > practice timezone) */
  effectiveTimezone: string;
  /** The practice/organization timezone setting */
  practiceTimezone: string | undefined;
  /** The user's personal timezone preference (if set) */
  userTimezone: string | undefined;
  /** Whether the user is using their own timezone (vs practice default) */
  isUsingUserTimezone: boolean;
  /** Set the user's personal timezone preference */
  setUserTimezone: (timezone: string | undefined) => Promise<void>;
  /** Clear the user's timezone preference (revert to practice default) */
  clearUserTimezone: () => Promise<void>;
  /** Format a date in the effective timezone */
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  /** Format a time in the effective timezone */
  formatTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  /** Format a date and time in the effective timezone */
  formatDateTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  /** Get the timezone label for display */
  getTimezoneLabel: (timezone: string) => string;
  /** Check if a timezone is valid */
  isValidTimezone: (timezone: string) => boolean;
  /** Loading state while fetching preferences */
  loading: boolean;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

interface TimezoneProviderProps {
  readonly children: ReactNode;
}

/**
 * Validate that a timezone string is valid
 */
function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get a display label for a timezone
 */
function getTimezoneLabel(timezone: string): string {
  const found = TIMEZONES.find((tz) => tz.value === timezone);
  if (found) return found.label;
  // For custom timezones, just return the value
  return timezone;
}

export function TimezoneProvider({ children }: TimezoneProviderProps): JSX.Element {
  const medplum = useMedplum();
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [userTimezone, setUserTimezoneState] = useState<string | undefined>();
  const [practiceTimezone, setPracticeTimezone] = useState<string | undefined>();

  // Load practice timezone from organization settings
  useEffect(() => {
    if (currentOrganization) {
      const settings = getAppSettings(currentOrganization);
      if (settings?.timezone && isValidTimezone(settings.timezone)) {
        setPracticeTimezone(settings.timezone);
      } else {
        setPracticeTimezone(undefined);
      }
    } else {
      setPracticeTimezone(undefined);
    }
  }, [currentOrganization]);

  // Load user timezone from practitioner preferences
  useEffect(() => {
    const loadUserTimezone = async (): Promise<void> => {
      try {
        const profile = medplum.getProfile() as Practitioner | undefined;
        if (profile) {
          const prefs = getUserPreferences(profile);
          if (prefs?.timezone && isValidTimezone(prefs.timezone)) {
            setUserTimezoneState(prefs.timezone);
          } else {
            setUserTimezoneState(undefined);
          }
        }
      } catch (err) {
        console.error('Failed to load user timezone preference:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!medplum.isLoading()) {
      loadUserTimezone();
    }
  }, [medplum]);

  // Calculate effective timezone
  const effectiveTimezone = useMemo(() => {
    // Priority: user preference > practice timezone (required - no browser fallback)
    if (userTimezone && isValidTimezone(userTimezone)) {
      return userTimezone;
    }
    if (practiceTimezone && isValidTimezone(practiceTimezone)) {
      return practiceTimezone;
    }
    // Practice timezone is required - use default only during initial setup
    return DEFAULT_TIMEZONE;
  }, [userTimezone, practiceTimezone]);

  const isUsingUserTimezone = !!userTimezone;

  // Save user timezone preference
  const setUserTimezone = useCallback(
    async (timezone: string | undefined): Promise<void> => {
      const profile = medplum.getProfile() as Practitioner | undefined;
      if (!profile?.id) return;

      try {
        const currentPractitioner = await medplum.readResource('Practitioner', profile.id);
        const existingPrefs = getUserPreferences(currentPractitioner);
        const mergedPrefs = mergeUserPreferences(existingPrefs, { timezone });
        const preferencesExtension = buildUserPreferencesExtension(mergedPrefs);
        const updatedPractitioner = setPractitionerExtension(
          currentPractitioner,
          USER_EXTENSION_URLS.userPreferences,
          preferencesExtension
        );
        await medplum.updateResource(updatedPractitioner);
        setUserTimezoneState(timezone);

        showNotification({
          color: 'green',
          message: timezone
            ? `Timezone set to ${getTimezoneLabel(timezone)}`
            : 'Timezone reset to practice default',
        });
      } catch (err) {
        console.error('Failed to save timezone preference:', err);
        showNotification({
          color: 'red',
          message: 'Failed to save timezone preference',
        });
      }
    },
    [medplum]
  );

  // Clear user timezone preference
  const clearUserTimezone = useCallback(async (): Promise<void> => {
    await setUserTimezone(undefined);
  }, [setUserTimezone]);

  // Format a date in the effective timezone
  const formatDate = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: effectiveTimezone,
      };
      return d.toLocaleDateString('en-US', { ...defaultOptions, ...options });
    },
    [effectiveTimezone]
  );

  // Format a time in the effective timezone
  const formatTime = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const defaultOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: effectiveTimezone,
      };
      return d.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
    },
    [effectiveTimezone]
  );

  // Format a date and time in the effective timezone
  const formatDateTime = useCallback(
    (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const d = typeof date === 'string' ? new Date(date) : date;
      const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: effectiveTimezone,
      };
      return d.toLocaleString('en-US', { ...defaultOptions, ...options });
    },
    [effectiveTimezone]
  );

  const value = useMemo(
    (): TimezoneContextValue => ({
      effectiveTimezone,
      practiceTimezone,
      userTimezone,
      isUsingUserTimezone,
      setUserTimezone,
      clearUserTimezone,
      formatDate,
      formatTime,
      formatDateTime,
      getTimezoneLabel,
      isValidTimezone,
      loading,
    }),
    [
      effectiveTimezone,
      practiceTimezone,
      userTimezone,
      isUsingUserTimezone,
      setUserTimezone,
      clearUserTimezone,
      formatDate,
      formatTime,
      formatDateTime,
      loading,
    ]
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

export function useTimezone(): TimezoneContextValue {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}
